import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import AdminBreadcrumb from '../../../components/admin/common/AdminBreadcrumb';
import AdminHeader from '../../../components/admin/common/AdminHeader';
import CategoryForm from '../../../components/admin/forms/CategoryForm';
import DeleteConfirmModal from '../../../components/admin/common/DeleteConfirmModal';

interface Category {
  id: string;
  name: string;
  description: string;
  slug: string;
  icon: string;
  featured: boolean;
  parentCategory?: string;
  gsmRange?: string;
  paperSizes?: string[];
  minOrderQuantity?: number;
  availability: 'in_stock' | 'low_stock' | 'out_of_stock';
  specifications?: {
    finish?: string;
    color?: string;
    thickness?: string;
    packaging?: string;
  };
  brands?: string[];
}

const EditCategory: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mock data - replace with actual API call
  const category: Category = {
    id: '1',
    name: 'Printing Paper',
    description: 'Premium quality papers for all printing needs',
    slug: 'printing-paper',
    icon: 'file-text',
    featured: true,
    gsmRange: '70-100',
    paperSizes: ['A4', 'A3', 'Letter', 'Legal'],
    minOrderQuantity: 1,
    availability: 'in_stock',
    specifications: {
      finish: 'Matte',
      color: 'White',
      thickness: '100 microns',
      packaging: '500 sheets per ream'
    },
    brands: ['Double A', 'HP', 'Xerox', 'Navigator']
  };

  const handleSubmit = async (data: any) => {
    setIsLoading(true);
    setError(null);

    try {
      // API call to update category
      console.log('Updating category:', data);
      navigate('/admin/categories');
    } catch (error) {
      setError('Failed to update category. Please try again.');
      console.error('Failed to update category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      // API call to delete category
      console.log('Deleting category:', id);
      setIsDeleteModalOpen(false);
      navigate('/admin/categories');
    } catch (error) {
      setError('Failed to delete category. Please try again.');
      console.error('Failed to delete category:', error);
    }
  };

  return (
    <div className="space-y-6">
      <AdminHeader
        title={`Edit Category: ${category.name}`}
        onMenuClick={() => {}}
        actions={
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/admin/categories')}
              className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="px-4 py-2 text-black bg-red-500 rounded-lg hover:bg-red-600"
            >
              Delete Category
            </button>
          </div>
        }
      />

      <div className="p-6 bg-white rounded-lg shadow-sm">
        <AdminBreadcrumb
          items={[
            { label: 'Categories', href: '/admin/categories' },
            { label: category.name }
          ]}
        />

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg 
            flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="mt-6">
          <CategoryForm
            initialData={category}
            onSubmit={handleSubmit}
            isSubmitting={isLoading}
          />
        </div>

        {/* Category Specifications */}
        <div className="mt-8 pt-6 border-t">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Category Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {category.gsmRange && (
              <div>
                <h4 className="font-medium text-gray-700">GSM Range</h4>
                <p className="text-gray-600">{category.gsmRange}</p>
              </div>
            )}
            {category.paperSizes && (
              <div>
                <h4 className="font-medium text-gray-700">Available Sizes</h4>
                <div className="flex flex-wrap gap-2">
                  {category.paperSizes.map((size) => (
                    <span key={size} className="px-2 py-1 bg-gray-100 rounded text-sm">
                      {size}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {category.specifications && (
              <div className="md:col-span-2">
                <h4 className="font-medium text-gray-700 mb-2">Specifications</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(category.specifications).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-sm text-gray-500 capitalize">{key}</span>
                      <p className="text-gray-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Category"
        message="Are you sure you want to delete this category? All associated products will be uncategorized. This action cannot be undone."
        itemName={category.name}
      />
    </div>
  );
};

export default EditCategory;